import { updatePlpProductView } from "@/api/saleor/services/user";
import { cookieKeys, saleorPlpMobileViewType } from "@/apiUtils/constants";
import { getCookieClient } from "@/apiUtils/cookieClientUtils";
import { useCallback, useEffect, useState } from "react";

type GridLayout = {
  view: string;
  columns: string;
  gutter: string;
};

interface UseGridLayoutType {
  gridLayout: GridLayout;
  handleGridView: () => void;
  handleColumnView: () => void;
}

const layoutOptions: Record<string, { columns: string; gutter: string }> = {
  "grid-view": { columns: "col-6", gutter: "gx-4" },
  "column-view": { columns: "col-12", gutter: "gx-6" },
};

const saleorPlpMobileViewTypes: Record<string, string> = {
  [saleorPlpMobileViewType.GRID]: "grid-view",
  [saleorPlpMobileViewType.COLUMN]: "column-view",
};

/**
 * @description This Hook used to Layout manipulations
 *
 * @return {*} gridLayout, handleGridView, handleColumnView
 */
const useGridLayout = (): UseGridLayoutType => {
  const constructObject = useCallback((view: string) => {
    const currentView = saleorPlpMobileViewTypes[view];

    return {
      view: currentView,
      ...layoutOptions[currentView],
    };
  }, []);

  const [gridLayout, setGridLayout] = useState<GridLayout>(
    constructObject(saleorPlpMobileViewType.GRID),
  );

  useEffect(() => {
    const mobileView = getCookieClient(cookieKeys.PLP_MOBILE_VIEW);
    if (mobileView) {
      setGridLayout(constructObject(mobileView));
    }
  }, [constructObject]);

  const getSaleorPlpMobileViewType = (view: string): string | null =>
    Object.keys(saleorPlpMobileViewTypes).find((type) => saleorPlpMobileViewTypes[type] === view) ||
    null;

  /**
   * @description This function is used to set grid layout or column view
   *
   * @param {string} view
   */
  const setGridLayoutView = async (view: string) => {
    if (layoutOptions.hasOwnProperty(view)) {
      setGridLayout((prevLayout) => ({
        ...prevLayout,
        view,
        ...layoutOptions[view],
      }));

      // Update selected layout in cookie for guest and saleor for signed user
      const layoutType = getSaleorPlpMobileViewType(view);
      if (layoutType) {
        const currentView = await updatePlpProductView(layoutType);
        if (currentView) {
          setGridLayout(constructObject(currentView));
        }
      }
    }
  };

  return {
    gridLayout,
    handleGridView: () => setGridLayoutView("grid-view"),
    handleColumnView: () => setGridLayoutView("column-view"),
  };
};

export default useGridLayout;
